import React, { useEffect, useState, useRef, useContext } from "react";
import { Minus, Plus, Trash2, Plus as PlusIcon, Loader2 } from "lucide-react";
import api from "../../../../api/axios";
import { AuthContext } from "../../../../Context/AuthContext";
import Swal from "sweetalert2";

const fmt = (n) => Number(n).toFixed(2);
const POS = () => {
  const { user } = useContext(AuthContext);
  const propertyCode = user?.property_name ?? null;

  const imageCacheRef = useRef({});
  const [modalSaveLoading, setModalSaveLoading] = useState(false);
  const [modalDeleteLoading, setModalDeleteLoading] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalButtonLoading, setModalButtonLoading] = useState(false);
  const [orderButtonLoading, setOrderButtonLoading] = useState(false);

  const [cartItems, setCartItems] = useState([]);

  const [tableNo, setTableNo] = useState("T-002");
  const [serviceType, setServiceType] = useState("table");
  const [selectedPayment, setSelectedPayment] = useState("Cash");
  const [roomId, setRoomId] = useState(null);
  const [reservedRooms, setReservedRooms] = useState([]);

  const [showItemManager, setShowItemManager] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [itemCategory, setItemCategory] = useState("");
  const [itemActive, setItemActive] = useState(true);

  const categories = [
    "Veg",
    "Non-Veg",
    "Snacks",
    "Breakfast",
    "Dinner",
    "Beverages",
    "Desserts",
  ];

  const [propertyInfo, setPropertyInfo] = useState(null);

  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceItemsSnapshot, setInvoiceItemsSnapshot] = useState([]);
  const [invoiceSubtotal, setInvoiceSubtotal] = useState(0);
  const [invoiceTax, setInvoiceTax] = useState(0);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  // -------------------- SECTION 3: FETCH PROPERTY + ITEMS --------------------
  const fetchPropertyInfo = async () => {
    if (!propertyCode) return;
    try {
      const res = await api.get(`property/${propertyCode}`);
      setPropertyInfo(res.data);
    } catch (err) {
      console.error("Property load error", err);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const res = await api.get("pms/pos/items");
      setMenuItems(res.data ?? res.data.items ?? res.data ?? []);
    } catch (err) {
      console.log(err);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyInfo();
    fetchMenuItems();
  }, [propertyCode]);
  // -------------------- SECTION 4: AVATAR IMAGE COMPONENT --------------------
  const LoadFoodImage = ({ name }) => {
    return (
      <div
        style={{
          width: "100%",
          height: 150,
          background: "#f2f2f2",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: 48,
          fontWeight: 700,
          color: "#5b5b5b",
          userSelect: "none",
        }}
      >
        {name ? name.charAt(0).toUpperCase() : "?"}
      </div>
    );
  };
  // -------------------- SECTION 5: ITEM MANAGER LOGIC --------------------
  const clearItemForm = () => {
    setEditItem(null);
    setItemName("");
    setItemDescription("");
    setItemPrice("");
    setItemCategory("");
    setItemActive(true);
  };

  const startEditItem = (item) => {
    setEditItem(item);
    setItemName(item.name);
    setItemDescription(item.description ?? "");
    setItemPrice(item.price);
    setItemCategory(item.category);
    setItemActive(item.is_active == 1);
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      setModalButtonLoading(true);
      await api.delete(`pms/pos/items/${id}`);
      setMenuItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      alert("Delete failed");
    } finally {
      setModalButtonLoading(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemName || !itemPrice || !itemCategory) {
      alert("Name, Price, Category required");
      return;
    }

    const payload = {
      property_code: propertyCode,
      name: itemName,
      description: itemDescription,
      price: itemPrice,
      category: itemCategory,
      is_active: itemActive ? 1 : 0,
    };

    try {
      setModalSaveLoading(true);

      if (editItem) {
        await api.put(`pms/pos/items/${editItem.id}`, payload);
      } else {
        await api.post("pms/pos/items", payload);
      }

      await fetchMenuItems();
      clearItemForm();
    } finally {
      setModalSaveLoading(false);
    }
  };
  // -------------------- SECTION 6: CART + THERMAL PRINT --------------------
  const handleAddToCart = (item) => {
    setCartItems((prev) => {
      const found = prev.find((c) => c.id === item.id);
      if (found) {
        return prev.map((c) =>
          c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const handleIncrement = (id) =>
    setCartItems((prev) =>
      prev.map((c) => (c.id === id ? { ...c, quantity: c.quantity + 1 } : c))
    );

  const handleDecrement = (id) =>
    setCartItems((prev) =>
      prev.map((c) =>
        c.id === id && c.quantity > 1 ? { ...c, quantity: c.quantity - 1 } : c
      )
    );

  const handleDelete = (id) =>
    setCartItems((prev) => prev.filter((c) => c.id !== id));

  const subtotal = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const tax = +(subtotal * 0.05).toFixed(2);
  const total = subtotal + tax;

  const printInvoice = (data, itemsSnapshot) => {
    const win = window.open("", "PRINT", "height=600,width=400");

    if (!win) {
      alert("Popup blocked. Please enable pop-ups.");
      return;
    }

    win.document.write(`
    <html>
      <head>
        <title>Print</title>
        <style>
          body { font-family: monospace; font-size: 12px; padding: 10px; }
          .row { display:flex; justify-content:space-between; }
          hr { border:none; border-top:1px dashed #444; margin:8px 0; }
        </style>
      </head>
      <body>
        <h3 style="text-align:center;">${propertyInfo?.name ?? "HOTEL"}</h3>
        <div>${propertyInfo?.address ?? ""}</div>
        <div>Phone: ${propertyInfo?.phone ?? ""}</div>
        <hr/>

        <div><strong>Date:</strong> ${new Date().toLocaleString()}</div>
        <div><strong>Payment:</strong> ${selectedPayment}</div>
        ${
          serviceType === "table"
            ? `<div><strong>Table:</strong> ${tableNo}</div>`
            : `<div><strong>Room:</strong> ${roomId ?? "-"}</div>`
        }
        <hr/>
  `);

    itemsSnapshot.forEach((i) => {
      const amount = i.line_total ?? i.price * i.quantity;

      win.document.write(`
      <div class="row">
        <span>${i.name} x ${i.quantity}</span>
        <span>₹${amount.toFixed(2)}</span>
      </div>
    `);
    });

    win.document.write(`
        <hr/>
        <div class="row"><strong>Subtotal</strong><strong>₹${invoiceSubtotal.toFixed(
          2
        )}</strong></div>
        <div class="row"><strong>Tax</strong><strong>₹${invoiceTax.toFixed(
          2
        )}</strong></div>
        <div class="row" style="font-size:14px;"><strong>Total</strong><strong>₹${invoiceTotal.toFixed(
          2
        )}</strong></div>
        <hr/>
        <div style="text-align:center;">Thank You!</div>
      </body>
    </html>
  `);

    win.document.close();
    win.focus();

    setTimeout(() => {
      win.print();
      win.close();
    }, 300);
  };

  const handleFinalPlaceOrder = async () => {
    if (!customerName.trim()) return alert("Customer name required");
    if (!customerMobile.trim()) return alert("Mobile number required");

    const payload = {
      invoice_no: null,
      reservation_id: serviceType === "room" ? roomId : null,
      service_type: serviceType,
      table_no: serviceType === "table" ? tableNo : null,
      room_no: serviceType === "room" ? roomId : null,
      customer_name: customerName,
      customer_mobile: customerMobile,
      customer_email: customerEmail,
      subtotal,
      tax,
      total,
      payment_mode: selectedPayment,

      items: cartItems.map((i) => ({
        pos_item_id: i.id,
        name: i.name,
        unit_price: i.price,
        quantity: i.quantity,
        line_total: i.price * i.quantity,
      })),
    };

    try {
      setOrderButtonLoading(true);
      const res = await api.post("pms/pos/sales", payload);
      setInvoiceSubtotal(subtotal);
      setInvoiceTax(tax);
      setInvoiceTotal(total);
      setInvoiceItemsSnapshot(cartItems);
      setInvoiceData(res.data);
      setShowCustomerModal(false);
      Swal.fire({
        icon: "success",
        title: "Order Placed Successfully!",
        confirmButtonText: "OK",
      }).then(() => {
        setShowInvoice(true);
        setCartItems([]);
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Order failed",
        text: err.response?.data?.message,
      });
    } finally {
      setOrderButtonLoading(false);
    }
  };
  // -------------------- SECTION 7: MENU UI --------------------
  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between mb-3">
        <h3 className="fw-bold">POS</h3>

        <button
          className="btn btn-dark d-flex align-items-center gap-2"
          onClick={() => {
            clearItemForm();
            setShowItemManager(true);
          }}
        >
          Edit Item
        </button>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <h4 className="fw-bold mb-3">Menu</h4>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center w-100 py-5">
              <div className="spinner-border text-success" role="status"></div>
            </div>
          ) : (
            <div className="row g-3">
              {menuItems
                .filter((item) => item.is_active == 1) // Show ONLY active items
                .map((item) => (
                  <div key={item.id} className="col-6 col-md-4 col-lg-3">
                    <div
                      className="shadow-sm rounded border"
                      style={{
                        overflow: "hidden",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          height: 90,
                          background: "#f5f5f5",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          fontSize: 36,
                          fontWeight: 700,
                          color: "#555",
                        }}
                      >
                        {item.name.charAt(0).toUpperCase()}
                      </div>

                      {/* Card Body */}
                      <div style={{ padding: "8px 10px" }}>
                        {/* Name */}
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: "#222",
                            marginBottom: 2,
                          }}
                        >
                          {item.name}
                        </div>

                        {/* Category */}
                        <div
                          style={{
                            fontSize: 12,
                            color: "#777",
                            marginBottom: 4,
                          }}
                        >
                          {item.category}
                        </div>

                        {/* Price */}
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#000",
                            marginBottom: 8,
                          }}
                        >
                          ₹{Number(item.price).toFixed(2)}
                        </div>

                        {/* Add Button */}
                        <button
                          className="btn btn-primary btn-sm"
                          style={{
                            width: "100%",
                            fontSize: 12,
                            padding: "4px 0",
                          }}
                          onClick={() => handleAddToCart(item)}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="col-lg-4 mt-4 mt-lg-0">
          <div className="card p-3 shadow-sm">
            <div className="d-flex gap-2 mb-3">
              <button
                className={`btn w-50 ${
                  serviceType === "table" ? "btn-dark" : "btn-outline-dark"
                }`}
                onClick={() => setServiceType("table")}
              >
                Table
              </button>

              <button
                className={`btn w-50 ${
                  serviceType === "room" ? "btn-dark" : "btn-outline-dark"
                }`}
                onClick={() => setServiceType("room")}
              >
                Room
              </button>
            </div>

            {serviceType === "room" ? (
              <select
                className="form-control"
                value={roomId || ""}
                onChange={(e) => setRoomId(e.target.value)}
              >
                <option value="">Select Room</option>
                {reservedRooms.map((r) => (
                  <option key={r.room_id} value={r.room_id}>
                    Room {r.room_no}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="form-control"
                value={tableNo}
                onChange={(e) => setTableNo(e.target.value)}
              />
            )}

            <div className="mt-3" style={{ maxHeight: 300, overflowY: "auto" }}>
              {!cartItems.length ? (
                <p className="text-center text-muted">Cart Empty</p>
              ) : (
                cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="d-flex justify-content-between mb-2"
                  >
                    <div>
                      <strong>{item.name}</strong>
                      <br />
                      <small>₹{fmt(item.price * item.quantity)}</small>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => handleDecrement(item.id)}
                      >
                        <Minus size={14} />
                      </button>

                      <span>{item.quantity}</span>

                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => handleIncrement(item.id)}
                      >
                        <Plus size={14} />
                      </button>

                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* PAYMENT MODE */}
            <div className="mt-3">
              <label className="form-label fw-bold">Payment Mode</label>

              <div className="d-flex gap-2">
                {/* TABLE: Cash + UPI */}
                {serviceType === "table" && (
                  <>
                    <button
                      className={`btn ${
                        selectedPayment === "Cash"
                          ? "btn-dark"
                          : "btn-outline-dark"
                      } w-50`}
                      onClick={() => setSelectedPayment("Cash")}
                    >
                      Cash
                    </button>

                    <button
                      className={`btn ${
                        selectedPayment === "UPI"
                          ? "btn-dark"
                          : "btn-outline-dark"
                      } w-50`}
                      onClick={() => setSelectedPayment("UPI")}
                    >
                      UPI
                    </button>
                  </>
                )}

                {/* ROOM: Cash + UPI + Card */}
                {serviceType === "room" && (
                  <>
                    <button
                      className={`btn ${
                        selectedPayment === "Cash"
                          ? "btn-dark"
                          : "btn-outline-dark"
                      } w-50`}
                      onClick={() => setSelectedPayment("pay_later")}
                    >
                      Pay Later
                    </button>

                    <button
                      className={`btn ${
                        selectedPayment === "UPI"
                          ? "btn-dark"
                          : "btn-outline-dark"
                      } w-50`}
                      onClick={() => setSelectedPayment("UPI")}
                    >
                      UPI
                    </button>

                    <button
                      className={`btn ${
                        selectedPayment === "Card"
                          ? "btn-dark"
                          : "btn-outline-dark"
                      } w-50`}
                      onClick={() => setSelectedPayment("Cash")}
                    >
                      Cash
                    </button>
                  </>
                )}
              </div>
            </div>

            <hr />

            <div className="d-flex justify-content-between">
              <span>Subtotal</span>
              <strong>₹{fmt(subtotal)}</strong>
            </div>

            <div className="d-flex justify-content-between">
              <span>Tax (5%)</span>
              <strong>₹{fmt(tax)}</strong>
            </div>

            <div className="d-flex justify-content-between mt-2 fw-bold fs-5">
              <span>Total</span>
              <span>₹{fmt(total)}</span>
            </div>

            <button
              className="btn btn-success mt-3 d-flex justify-content-center align-items-center gap-2"
              disabled={orderButtonLoading}
              // onClick={handlePlaceOrder}
              onClick={() => {
                if (!cartItems.length) return alert("Cart empty");
                setShowCustomerModal(true);
              }}
            >
              {orderButtonLoading && <Loader2 className="spin" size={20} />}
              Place Order
            </button>
          </div>
        </div>
      </div>
      {/* ---------------- THERMAL RECEIPT PREVIEW ---------------- */}
      {showInvoice && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 99999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 10,
          }}
          onClick={() => setShowInvoice(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              width: 260, // 58mm preview width
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "12px 14px",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              fontFamily: "monospace",
              fontSize: 12,
              color: "#000",
            }}
          >
            {/* HEADER */}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: "bold", fontSize: 14 }}>
                {propertyInfo?.name ?? "HOTEL NAME"}
              </div>

              <div style={{ fontSize: 11, marginTop: 2 }}>
                {propertyInfo?.address}
              </div>

              <div style={{ fontSize: 11 }}>PHONE : {propertyInfo?.phone}</div>

              {propertyInfo?.gstin && (
                <div style={{ fontSize: 11 }}>GSTIN : {propertyInfo.gstin}</div>
              )}

              <div
                style={{
                  fontWeight: "bold",
                  marginTop: 6,
                  textDecoration: "underline",
                }}
              >
                Retail Invoice
              </div>
            </div>

            {/* INVOICE META */}
            <div style={{ marginTop: 10 }}>
              <div>
                <strong>Date :</strong> {new Date().toLocaleString()}
              </div>
              <div>
                <strong>Payment Mode :</strong> {selectedPayment}
              </div>
              {serviceType === "table" ? (
                <div>
                  <strong>Table :</strong> {tableNo}
                </div>
              ) : (
                <div>
                  <strong>Room :</strong> {roomId ?? "-"}
                </div>
              )}
            </div>

            <div
              style={{
                borderTop: "1px dashed #444",
                margin: "8px 0",
              }}
            />

            {/* ITEM TABLE */}
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Item</th>
                  <th style={{ textAlign: "center" }}>Qty</th>
                  <th style={{ textAlign: "right" }}>Amt</th>
                </tr>
              </thead>

              <tbody>
                {invoiceItemsSnapshot.map((it, idx) => (
                  <tr key={idx}>
                    <td style={{ textAlign: "left" }}>{it.name}</td>
                    <td style={{ textAlign: "center" }}>{it.quantity}</td>
                    <td style={{ textAlign: "right" }}>
                      ₹{Number(it.line_total).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              style={{
                borderTop: "1px dashed #444",
                margin: "8px 0",
              }}
            />

            {/* TOTALS */}
            <table style={{ width: "100%" }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: "bold" }}>Sub Total</td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    ₹{fmt(invoiceSubtotal)}
                  </td>
                </tr>

                <tr>
                  <td style={{ fontWeight: "bold" }}>Tax</td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    ₹{fmt(invoiceTax)}
                  </td>
                </tr>

                <tr>
                  <td
                    style={{
                      fontWeight: "bold",
                      fontSize: 14,
                      paddingTop: 6,
                    }}
                  >
                    TOTAL
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: 14,
                      paddingTop: 6,
                    }}
                  >
                    ₹{fmt(invoiceTotal)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              style={{
                borderTop: "1px dashed #444",
                margin: "8px 0",
              }}
            />

            {/* FOOTER */}
            <table style={{ width: "100%" }}>
              <tbody>
                <tr>
                  <td>Cash :</td>
                  <td style={{ textAlign: "right" }}>₹{fmt(invoiceTotal)}</td>
                </tr>

                <tr>
                  <td>Cash tendered :</td>
                  <td style={{ textAlign: "right" }}>₹{fmt(invoiceTotal)}</td>
                </tr>
              </tbody>
            </table>

            <div
              style={{
                borderTop: "1px dashed #444",
                margin: "8px 0",
              }}
            />

            <div
              style={{
                textAlign: "center",
                fontSize: 11,
                marginTop: 4,
              }}
            >
              E & O E
            </div>

            {/* BUTTONS */}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                className="btn btn-outline-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowInvoice(false)}
              >
                Close
              </button>

              <button
                className="btn btn-dark"
                style={{ flex: 1 }}
                onClick={() => printInvoice(invoiceData, invoiceItemsSnapshot)}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- ITEM MANAGER MODAL ---------------- */}
      {showItemManager && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">POS Items Manager</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowItemManager(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    {/* ---------- LEFT SIDE: ITEM LIST ---------- */}
                    <div
                      className="col-md-7"
                      style={{ maxHeight: "60vh", overflowY: "auto" }}
                    >
                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <strong>Items</strong>
                        <div>
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() => fetchMenuItems()}
                          >
                            Refresh
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => clearItemForm()}
                          >
                            New Item
                          </button>
                        </div>
                      </div>

                      <div className="list-group">
                        {menuItems.length === 0 ? (
                          <div className="text-muted p-3">No items found</div>
                        ) : (
                          menuItems.map((mi) => (
                            <div
                              key={mi.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div style={{ fontWeight: 600 }}>{mi.name}</div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: 13 }}
                                >
                                  {mi.category} · ₹{fmt(mi.price)}
                                </div>
                              </div>

                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => startEditItem(mi)}
                                >
                                  Edit
                                </button>

                                {/* <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteItem(mi.id)}
                                  disabled={modalButtonLoading}
                                >
                                  {modalButtonLoading ? "..." : "Delete"}
                                </button> */}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* ---------- RIGHT SIDE: EDIT / ADD FORM ---------- */}
                    <div className="col-md-5">
                      <div
                        style={{
                          borderLeft: "1px solid #eee",
                          paddingLeft: 16,
                        }}
                      >
                        <h6>{editItem ? "Edit Item" : "Add Item"}</h6>

                        <div className="mb-2">
                          <label className="form-label small">Name</label>
                          <input
                            className="form-control"
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                          />
                        </div>

                        <div className="mb-2">
                          <label className="form-label small">
                            Description
                          </label>
                          <textarea
                            rows={2}
                            className="form-control"
                            value={itemDescription}
                            onChange={(e) => setItemDescription(e.target.value)}
                          />
                        </div>

                        <div className="mb-2">
                          <label className="form-label small">Price</label>
                          <input
                            type="number"
                            className="form-control"
                            value={itemPrice}
                            onChange={(e) => setItemPrice(e.target.value)}
                          />
                        </div>

                        <div className="mb-2">
                          <label className="form-label small">Category</label>
                          <select
                            className="form-select"
                            value={itemCategory}
                            onChange={(e) => setItemCategory(e.target.value)}
                          >
                            <option value="">Select Category</option>
                            {categories.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-check form-switch mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={itemActive}
                            onChange={(e) => setItemActive(e.target.checked)}
                          />
                          <label className="form-check-label">Active</label>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-secondary"
                            onClick={() => clearItemForm()}
                          >
                            Reset
                          </button>

                          <button
                            className="btn btn-primary"
                            onClick={handleSaveItem}
                            disabled={modalSaveLoading}
                          >
                            {modalSaveLoading
                              ? "Saving..."
                              : editItem
                              ? "Update Item"
                              : "Add Item"}
                          </button>
                        </div>

                        {editItem && (
                          <div className="mt-3">
                            <small className="text-muted">
                              Editing: {editItem.name} (id: {editItem.id})
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setShowItemManager(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* CUSTOMER DETAILS MODAL */}
      {showCustomerModal && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Customer Details</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowCustomerModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Customer Name</label>
                    <input
                      className="form-control"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Mobile No</label>
                    <input
                      className="form-control"
                      value={customerMobile}
                      onChange={(e) => setCustomerMobile(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowCustomerModal(false)}
                  >
                    Close
                  </button>

                  <button
                    className="btn btn-dark"
                    onClick={handleFinalPlaceOrder}
                  >
                    Confirm & Place Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default POS;
